import React, { FormEvent, useState, useEffect } from "react";
import { useHistory, useLocation, Link } from "react-router-dom";
import queryString from "query-string";
import { apiV2Service } from "../../../services/apiV2Service";
import { usePostAuthFlow } from "../../../shared/auth";
import * as localService from "../../../services/localService";
import { showApiMessage } from "../../../utils/messageHandler";
import { FaRegEye as FaEye, FaRegEyeSlash as FaEyeSlash } from "react-icons/fa";
import logger from "../../../utils/logger";
import bg from "../../../assets/img/spacia/web/Background.png";
import logo from "../../../assets/img/spacia/web/spacia_logo.png";
import { V2PasswordPolicy } from "../../../types/v2types";
import {
  buildConfirmAccountDeepLink,
  buildLoginDeepLink,
  isMobileDevice,
  openDeepLink,
  detectPlatform,
  getAppStoreUrl,
} from "../../../utils/deepLink";
import { RoleConstants } from "../../../utils/constants";
import { SpaciaPasswordGuidelines } from "../../../components/SpaciaPasswordGuidelines";

interface AuthResponse {
  access_token: string;
  refresh_token: string;
  user: any;
}

const OTPConfirmation = () => {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [requestCompleted, setRequestCompleted] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");
  const [showError, setShowError] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordPolicy, setPasswordPolicy] = useState<V2PasswordPolicy | null>(null);
  const [passwordErrors, setPasswordErrors] = useState<string[]>([]);
  const [isResending, setIsResending] = useState(false);
  const [resendMessage, setResendMessage] = useState("");
  const [setupCompleted, setSetupCompleted] = useState(false);
  const [mobilePromptVisible, setMobilePromptVisible] = useState(false);
  const [mobileDeepLinkUrl, setMobileDeepLinkUrl] = useState<string | null>(null);
  const [postAuthRedirectPath, setPostAuthRedirectPath] = useState<string | null>(null);
  const [pendingPostAuthResponse, setPendingPostAuthResponse] = useState<AuthResponse | null>(null);
  const [continueOnWebLoading, setContinueOnWebLoading] = useState(false);
  const [mobileFlowError, setMobileFlowError] = useState<string | null>(null);

  const history = useHistory();
  const location = useLocation();

  const search = queryString.parse(location.search);
  const token = search.code as string;
  const inviteQueryValue = (search.invite as string) || (search.isInviteFlow as string);
  const isInviteFlow = inviteQueryValue === "1" || inviteQueryValue === "true";

  // Check if user came from login page (password change required)
  const pendingEmail = localService.getItem("pendingPasswordChangeEmail");
  const isPasswordChangeRequired = !!pendingEmail;

  // Get shared post-auth flow
  const { handlePostAuthFlow } = usePostAuthFlow();

  // Fetch password policy on component mount
  useEffect(() => {
    const fetchPasswordPolicy = async () => {
      try {
        const response = await apiV2Service.getDefaultPasswordPolicy();
        if (response.status === 200) {
          setPasswordPolicy(response.data.data);
        }
      } catch (error) {
        logger.error("Failed to fetch password policy:", error);
      }
    };
    fetchPasswordPolicy();
  }, []);

  // Auto-confirm email if token is present and it's not an invite flow
  useEffect(() => {
    const autoConfirmEmail = async () => {
      if (token && !isInviteFlow && !isPasswordChangeRequired) {
        try {
          logger.log("[OTPConfirmation] Auto-confirming email with token");
          await apiV2Service.confirmEmail(token);
          logger.log("[OTPConfirmation] Auto-email confirmation successful");
        } catch (error) {
          // This might be expected if the token is for something else or already used
          logger.warn("[OTPConfirmation] Auto-email confirmation failed (may be expected):", error);
        }
      }
    };
    autoConfirmEmail();
  }, [token, isInviteFlow, isPasswordChangeRequired]);

  // Handle resend confirmation
  const handleResendConfirmation = async () => {
    if (!pendingEmail) {
      setResendMessage("No email found to resend confirmation");
      return;
    }

    setIsResending(true);
    setResendMessage("");

    try {
      // Use the correct API endpoint for resending account confirmation
      const response = await apiV2Service.resendAccountConfirmation({
        username: pendingEmail,
        confirmationUrl: `${window.location.origin}/?action=confirm_account`,
      });

      // Use message handler for success
      showApiMessage(response, "resend confirmation");
      setResendMessage(""); // Clear any previous error messages
    } catch (error: any) {
      logger.error("Failed to resend confirmation:", error);

      // Use message handler for errors
      showApiMessage(error, "resend confirmation");

      // Also set a local message for the UI feedback
      const errorData = error?.response?.data;
      if (errorData?.error === "NOT_FOUND") {
        setResendMessage("User account not found. Please check your email address.");
      } else if (errorData?.error === "USER_NOT_PENDING_CONFIRMATION") {
        setResendMessage("Your account is already confirmed. You can sign in normally.");
      } else {
        setResendMessage("Failed to send confirmation email. Please try again.");
      }
    } finally {
      setIsResending(false);
    }
  };

  const validate = () => {
    const errors: string[] = [];

    if (!password || !confirmPassword) {
      errors.push("Please enter and confirm your password.");
    }

    if (password !== confirmPassword) {
      errors.push("Passwords do not match.");
    }

    // Apply password policy validation if available
    if (passwordPolicy && password) {
      if (password.length < passwordPolicy.minLength) {
        errors.push(`Password must be at least ${passwordPolicy.minLength} characters.`);
      }
      if (passwordPolicy.maxLength > 0 && password.length > passwordPolicy.maxLength) {
        errors.push(`Password must be no more than ${passwordPolicy.maxLength} characters.`);
      }
      if (passwordPolicy.minUppercaseCount > 0) {
        const uppercaseCount = (password.match(/[A-Z]/g) || []).length;
        if (uppercaseCount < passwordPolicy.minUppercaseCount) {
          errors.push(`Password must contain at least ${passwordPolicy.minUppercaseCount} uppercase letter(s).`);
        }
      }
      if (passwordPolicy.minLowercaseCount > 0) {
        const lowercaseCount = (password.match(/[a-z]/g) || []).length;
        if (lowercaseCount < passwordPolicy.minLowercaseCount) {
          errors.push(`Password must contain at least ${passwordPolicy.minLowercaseCount} lowercase letter(s).`);
        }
      }
      if (passwordPolicy.minDigitCount > 0) {
        const digitCount = (password.match(/\d/g) || []).length;
        if (digitCount < passwordPolicy.minDigitCount) {
          errors.push(`Password must contain at least ${passwordPolicy.minDigitCount} digit(s).`);
        }
      }
      if (passwordPolicy.minSymbolCount > 0) {
        // eslint-disable-next-line no-useless-escape
        const symbolCount = (password.match(/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/\?]/g) || []).length;
        if (symbolCount < passwordPolicy.minSymbolCount) {
          errors.push(`Password must contain at least ${passwordPolicy.minSymbolCount} special character(s).`);
        }
      }
    }

    if (errors.length > 0) {
      setErrorMsg(errors.join(" "));
      setShowError(true);
      setPasswordErrors(errors);
      return false;
    }

    setShowError(false);
    setPasswordErrors([]);
    return true;
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!validate()) return;
    setRequestCompleted(false);

    try {
      const response = await apiV2Service.confirmInvite(token, password);
      if (!response || !response.access_token) {
        throw new Error("Invalid response from server");
      }

      // Show success message first
      showApiMessage(response, "confirm password");

      if (isInviteFlow) {
        if (isMobileDevice()) {
          try {
            const roleCode = response?.user?.organizationRole?.code || response?.user?.role || null;
            const orgTypeCode =
              response?.user?.organizationRole?.organizationType?.code ||
              response?.user?.organization?.organizationType?.code ||
              null;

            // Token is omitted here because web already handles confirmation.
            // App just needs action and flavor to open sign-in screen.
            const deepLinkUrl = buildConfirmAccountDeepLink({
              roleCode,
              orgTypeCode,
              isInvite: true, // Use invite_confirmation action
            });

            openDeepLink(deepLinkUrl, {
              redirectToAppStore: true, // Redirect to app store if deep link fails
            });
          } catch (deepLinkError) {
            logger.warn("Unable to open deep link after invite confirmation", deepLinkError);

            const platform = detectPlatform();
            const appStoreUrl = getAppStoreUrl(platform);

            // Fallback: redirect to app store
            if (appStoreUrl) {
              window.location.href = appStoreUrl;
            }
          }
        } else {
          history.replace("/signin?invite=success");
        }

        localService.clearAllUserData();
        return;
      }

      const onMobileDevice = isMobileDevice();
      if (onMobileDevice) {
        setSetupCompleted(true);
        setPendingPostAuthResponse(response);
        setMobileFlowError(null);

        const roleCode = response?.user?.organizationRole?.code || response?.user?.role || "";
        const orgTypeCode =
          response?.user?.organizationRole?.organizationType?.code ||
          response?.user?.organization?.organizationType?.code ||
          null;
        const redirectPath = RoleConstants.getNavigationPath(roleCode);
        setPostAuthRedirectPath(redirectPath);

        try {
          // Token is omitted here because web already handles confirmation.
          // App just needs action and flavor to open sign-in screen.
          const deepLinkUrl = buildLoginDeepLink(
            undefined, // flavor
            roleCode || null,
            orgTypeCode
          );

          setMobileDeepLinkUrl(deepLinkUrl);
        } catch (deepLinkError) {
          logger.warn("Unable to build confirm account deep link after password setup", deepLinkError);
          setMobileDeepLinkUrl(null);
        }

        setMobilePromptVisible(true);
        return;
      }

      // Use shared post-auth flow
      await handlePostAuthFlow(response);

      // Set flag to show host tour if user is a host (HOST_PROVIDER org type)
      const user = localService.getUser();
      const orgTypeCode = user?.organization?.type?.code;

      if (orgTypeCode === "HOST_PROVIDER") {
        localStorage.setItem("pendingHostTour", "true");
      }

      // postAuthFlow handles subscription checks and navigation for all org types
      // No need to manually check subscriptionTierId here
    } catch (err: any) {
      // Only show error message if it's actually an API error, not a post-auth flow issue
      if (err?.response?.status) {
        showApiMessage(err, "confirm password");
      } else {
        // For non-API errors (like post-auth flow issues), show a generic message
        const message = err?.message || "An error occurred during account setup.";
        setErrorMsg(message);
        setShowError(true);
        return;
      }

      const message = err?.response?.data?.message || err?.message || "An error occurred.";
      setErrorMsg(message);
      setShowError(true);
    } finally {
      setRequestCompleted(true);
    }
  };

  const handleOpenMobileApp = (): any => {
    if (!mobilePromptVisible) {
      return;
    }

    if (mobileDeepLinkUrl) {
      openDeepLink(mobileDeepLinkUrl, {
        redirectToAppStore: true, // Redirect to app store if deep link fails
        onFallback: () => {
          // This will only be called if redirectToAppStore is false or app store URL is not available
          setMobileFlowError("We couldn't open the mobile app. You can still continue in your browser.");
        },
      });
    } else {
      setMobileFlowError("This link is not available right now. Please continue in your browser.");
    }
  };

  const handleContinueInBrowser = async () => {
    if (!pendingPostAuthResponse) return;

    setContinueOnWebLoading(true);
    setMobileFlowError(null);
    try {
      await handlePostAuthFlow(pendingPostAuthResponse);
    } catch (err: any) {
      const message = err?.response?.data?.message || err?.message || "We couldn't finish signing you in.";
      setMobileFlowError(message);
    } finally {
      setContinueOnWebLoading(false);
    }
  };

  return (
    <div className="flex relative justify-center items-center w-full min-h-screen">
      <div
        className="fixed inset-0 z-0 bg-center bg-no-repeat bg-cover"
        style={{ backgroundImage: `url(${bg})` }}
      ></div>
      <div className="absolute top-8 left-8 z-10">
        <Link to="/home">
          <img src={logo} alt="Spacia Logo" className="h-10 md:h-12" />
        </Link>
      </div>
      <div className="relative z-10 p-6 mx-auto w-full max-w-md bg-white rounded-lg shadow-lg md:p-8">
        <h1 className="mb-6 text-xl font-semibold text-center text-gray-800 md:text-2xl">
          {isPasswordChangeRequired ? "Password Change Required" : "Set Your Password"}
        </h1>
        {isPasswordChangeRequired && (
          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-800 text-center">
              You need to change your password before you can access your account. Please check your email for a
              confirmation link or request a new one below.
            </p>
          </div>
        )}
        {isPasswordChangeRequired && !token && (
          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-md">
            <h3 className="text-sm font-medium text-blue-800 mb-2">Need a confirmation link?</h3>
            <p className="text-sm text-blue-700 mb-3">We can send you a new confirmation link to {pendingEmail}</p>
            <button
              type="button"
              onClick={handleResendConfirmation}
              disabled={isResending}
              className="w-full px-4 py-2 text-sm font-medium text-blue-700 bg-blue-100 border border-blue-300 rounded-md hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isResending ? "Sending..." : "Send Confirmation Link"}
            </button>
            {resendMessage && (
              <p
                className={`mt-2 text-xs ${resendMessage.includes("successfully") ? "text-green-600" : "text-red-600"}`}
              >
                {resendMessage}
              </p>
            )}
          </div>
        )}

        {token &&
          (!setupCompleted ? (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="password" className="block mb-2 text-sm font-medium text-gray-700">
                  Password
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    id="password"
                    className="px-4 pr-12 py-2 w-full rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={password}
                    onChange={e => {
                      const newPassword = e.target.value;
                      setPassword(newPassword);
                      // Re-validate if both passwords are filled and match
                      if (newPassword && confirmPassword && newPassword === confirmPassword) {
                        // Silently validate to clear errors if validation passes
                        const errors: string[] = [];
                        if (!newPassword || !confirmPassword) {
                          errors.push("Please enter and confirm your password.");
                        }
                        if (newPassword !== confirmPassword) {
                          errors.push("Passwords do not match.");
                        }
                        // Apply password policy validation if available
                        if (passwordPolicy && newPassword) {
                          if (newPassword.length < passwordPolicy.minLength) {
                            errors.push(`Password must be at least ${passwordPolicy.minLength} characters.`);
                          }
                          if (passwordPolicy.maxLength > 0 && newPassword.length > passwordPolicy.maxLength) {
                            errors.push(`Password must be no more than ${passwordPolicy.maxLength} characters.`);
                          }
                          if (passwordPolicy.minUppercaseCount > 0) {
                            const uppercaseCount = (newPassword.match(/[A-Z]/g) || []).length;
                            if (uppercaseCount < passwordPolicy.minUppercaseCount) {
                              errors.push(
                                `Password must contain at least ${passwordPolicy.minUppercaseCount} uppercase letter(s).`
                              );
                            }
                          }
                          if (passwordPolicy.minLowercaseCount > 0) {
                            const lowercaseCount = (newPassword.match(/[a-z]/g) || []).length;
                            if (lowercaseCount < passwordPolicy.minLowercaseCount) {
                              errors.push(
                                `Password must contain at least ${passwordPolicy.minLowercaseCount} lowercase letter(s).`
                              );
                            }
                          }
                          if (passwordPolicy.minDigitCount > 0) {
                            const digitCount = (newPassword.match(/\d/g) || []).length;
                            if (digitCount < passwordPolicy.minDigitCount) {
                              errors.push(`Password must contain at least ${passwordPolicy.minDigitCount} digit(s).`);
                            }
                          }
                          if (passwordPolicy.minSymbolCount > 0) {
                            const symbolCount = (newPassword.match(/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/\?]/g) || [])
                              .length;
                            if (symbolCount < passwordPolicy.minSymbolCount) {
                              errors.push(
                                `Password must contain at least ${passwordPolicy.minSymbolCount} special character(s).`
                              );
                            }
                          }
                        }
                        // Clear errors if validation passes
                        if (errors.length === 0) {
                          setShowError(false);
                          setPasswordErrors([]);
                        } else {
                          setPasswordErrors(errors);
                        }
                      } else {
                        // If passwords don't match or are empty, clear errors on typing
                        if (showError) {
                          setShowError(false);
                          setPasswordErrors([]);
                        }
                      }
                    }}
                    placeholder="Enter password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 flex items-center justify-center text-gray-400 hover:text-gray-600 focus:outline-none focus:text-gray-600"
                    style={{ transform: "translateY(calc(-50% + 0px))" }}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                    tabIndex={-1}
                  >
                    {showPassword ? <FaEye className="w-4 h-4" /> : <FaEyeSlash className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div>
                <label htmlFor="confirmPassword" className="block mb-2 text-sm font-medium text-gray-700">
                  Confirm Password
                </label>
                <div className="relative">
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    id="confirmPassword"
                    className="px-4 pr-12 py-2 w-full rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={confirmPassword}
                    onChange={e => {
                      const newConfirmPassword = e.target.value;
                      setConfirmPassword(newConfirmPassword);
                      // Re-validate if both passwords are filled and match
                      if (password && newConfirmPassword && password === newConfirmPassword) {
                        // Silently validate to clear errors if validation passes
                        const errors: string[] = [];
                        if (!password || !newConfirmPassword) {
                          errors.push("Please enter and confirm your password.");
                        }
                        if (password !== newConfirmPassword) {
                          errors.push("Passwords do not match.");
                        }
                        // Apply password policy validation if available
                        if (passwordPolicy && password) {
                          if (password.length < passwordPolicy.minLength) {
                            errors.push(`Password must be at least ${passwordPolicy.minLength} characters.`);
                          }
                          if (passwordPolicy.maxLength > 0 && password.length > passwordPolicy.maxLength) {
                            errors.push(`Password must be no more than ${passwordPolicy.maxLength} characters.`);
                          }
                          if (passwordPolicy.minUppercaseCount > 0) {
                            const uppercaseCount = (password.match(/[A-Z]/g) || []).length;
                            if (uppercaseCount < passwordPolicy.minUppercaseCount) {
                              errors.push(
                                `Password must contain at least ${passwordPolicy.minUppercaseCount} uppercase letter(s).`
                              );
                            }
                          }
                          if (passwordPolicy.minLowercaseCount > 0) {
                            const lowercaseCount = (password.match(/[a-z]/g) || []).length;
                            if (lowercaseCount < passwordPolicy.minLowercaseCount) {
                              errors.push(
                                `Password must contain at least ${passwordPolicy.minLowercaseCount} lowercase letter(s).`
                              );
                            }
                          }
                          if (passwordPolicy.minDigitCount > 0) {
                            const digitCount = (password.match(/\d/g) || []).length;
                            if (digitCount < passwordPolicy.minDigitCount) {
                              errors.push(`Password must contain at least ${passwordPolicy.minDigitCount} digit(s).`);
                            }
                          }
                          if (passwordPolicy.minSymbolCount > 0) {
                            const symbolCount = (password.match(/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/\?]/g) || [])
                              .length;
                            if (symbolCount < passwordPolicy.minSymbolCount) {
                              errors.push(
                                `Password must contain at least ${passwordPolicy.minSymbolCount} special character(s).`
                              );
                            }
                          }
                        }
                        // Clear errors if validation passes
                        if (errors.length === 0) {
                          setShowError(false);
                          setPasswordErrors([]);
                        } else {
                          setPasswordErrors(errors);
                        }
                      } else {
                        // If passwords don't match or are empty, clear errors on typing
                        if (showError) {
                          setShowError(false);
                          setPasswordErrors([]);
                        }
                      }
                    }}
                    placeholder="Confirm password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 flex items-center justify-center text-gray-400 hover:text-gray-600 focus:outline-none focus:text-gray-600"
                    style={{ transform: "translateY(calc(-50% + 0px))" }}
                    aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                    tabIndex={-1}
                  >
                    {showConfirmPassword ? <FaEye className="w-4 h-4" /> : <FaEyeSlash className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              {showError && <div className="text-sm text-center text-red-600">{errorMsg}</div>}

              {/* Password Policy Requirements */}
              <SpaciaPasswordGuidelines />

              <button
                type="submit"
                className={`w-full flex justify-center items-center py-2 px-4 border border-transparent rounded-md text-white font-medium ${
                  !password || !confirmPassword || passwordErrors.length > 0
                    ? "bg-gray-400 cursor-not-allowed"
                    : "bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                }`}
                disabled={!password || !confirmPassword || passwordErrors.length > 0 || !requestCompleted}
              >
                {!requestCompleted ? (
                  <span className="flex items-center">
                    <svg
                      className="mr-3 -ml-1 w-5 h-5 text-white animate-spin"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Setting Password...
                  </span>
                ) : (
                  "Set Password"
                )}
              </button>
            </form>
          ) : (
            <div className="space-y-4">
              <div className="p-4 text-center bg-green-50 border border-green-200 rounded-md">
                <h2 className="text-lg font-semibold text-green-700">Password set successfully</h2>
                <p className="mt-2 text-sm text-green-700">
                  You're all set! Choose how you'd like to continue using Spacia.
                </p>
              </div>

              {mobilePromptVisible && (
                <div className="space-y-3">
                  <button
                    type="button"
                    onClick={handleOpenMobileApp}
                    className="w-full px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    Continue in mobile app
                  </button>
                  <button
                    type="button"
                    onClick={handleContinueInBrowser}
                    disabled={continueOnWebLoading}
                    className={`w-full px-4 py-2 text-sm font-medium rounded-md border ${
                      continueOnWebLoading
                        ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                        : "bg-white text-blue-600 border-blue-400 hover:bg-blue-50"
                    }`}
                  >
                    {continueOnWebLoading ? "Continuing..." : "Continue in browser"}
                  </button>
                </div>
              )}

              {postAuthRedirectPath && (
                <p className="text-xs text-center text-gray-500">
                  We’ll take you to {postAuthRedirectPath} when you continue in the browser.
                </p>
              )}

              {mobileFlowError && <p className="text-sm text-center text-red-600">{mobileFlowError}</p>}
            </div>
          ))}

        <div className="mt-6 text-center">
          <Link to="/signin" className="text-sm text-blue-600 hover:text-blue-800 hover:underline">
            Return to sign in
          </Link>
        </div>
      </div>
    </div>
  );
};

export default OTPConfirmation;
